import { LinearProgress, Tooltip, Typography, Box } from '@vapor/material'
import { TextField } from '@vapor/material'
import { Autocomplete } from '@mui/material'
import { GridRenderEditCellParams, GridRowModes, GridRenderCellParams, useGridApiContext } from '@mui/x-data-grid-pro'
import { useCallback, useMemo } from 'react'
import { useAvailableResourcesQuery } from 'src/api/api.generated'
import { /* useHotelDate, */ useTenant } from 'src/app/hooks/context/context.hook'
import { ReservedResourceRow, ResourceRow } from './reserved-resources-grid.types'
import { useTranslation } from 'react-i18next'
// import { compareDates } from 'src/utils/date'

export type ResourceCellProps = GridRenderCellParams<any, ReservedResourceRow>

export default function ResourceCell({ row, field }: ResourceCellProps) {
  const { t } = useTranslation()
  const type = row.resource.type
  const resource = row.resource.resource
  const editable = row.editable
  const apiRef = useGridApiContext()

  const onClickHandler = useCallback(() => {
    apiRef.current.startCellEditMode({ id: row.id, field })
  }, [row])

  if (type === 'DATA') {
    return (
      <div style={{ cursor: editable ? 'pointer' : 'default', width: '100%' }}>
        {resource?.code ? (
          <Box onClick={onClickHandler}>
            <Typography variant="body">{resource?.code}</Typography>
          </Box>
        ) : (
          <div>
            <Typography color="gray" variant="body" onClick={onClickHandler}>
              <Tooltip title={editable ? t('pms.reservation.resource_assignement') : ''}>
                <div style={{ textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap', width: '100%' }}>{t('pms.booking.reservation.resource_not_assigned')}</div>
              </Tooltip>
            </Typography>
          </div>
        )}
      </div>
    )
  } else {
    return (
      <div style={{ width: '100%' }}>
        <div onClick={onClickHandler}>{resource?.code || '-'}</div>
        <div>
          <LinearProgress color="secondary" />
        </div>
      </div>
    )
  }
}

export type ResourceEditCellProps = Omit<GridRenderEditCellParams, 'row'> & { row: ReservedResourceRow }

export function ResourceEditCell(props: ResourceEditCellProps) {
  // const hotelDate = useHotelDate()

  const row = props.row
  const propertyId = row.propertyId
  const occupancy = row.currentResourceOccupancy
  const resourceTypeId = occupancy.assignedResourceType ? occupancy.assignedResourceType.id : occupancy?.reservedResourceType?.id
  const start = occupancy?.start
  const end = occupancy?.end
  if (propertyId && resourceTypeId && start && end) {
    return (
      <InnerResourceEditCell //
        propertyId={propertyId}
        resourceTypeId={resourceTypeId}
        start={start}
        end={end}
        disableClearable={row.editable && /* compareDates(row.start, hotelDate) < 0 && */ row.phase !== 'RESERVATION' ? true : undefined}
        {...props}
      />
    )
  } else {
    return <></>
  }
}

type InnerResourceEditCellProps = Omit<GridRenderEditCellParams, 'row'> & {
  propertyId: string
  resourceTypeId: string
  start: Date
  end: Date
  disableClearable?: true
}

type LocalResource =
  | {
      id: string
      code: string
    }
  | null
  | undefined

function InnerResourceEditCell(props: InnerResourceEditCellProps) {
  const { id, value, field, propertyId, resourceTypeId, start, end, disableClearable } = props
  const localValue: LocalResource = useMemo(() => {
    const resourceRow = value as ResourceRow
    return resourceRow.resource
  }, [value])

  const apiRef = useGridApiContext()
  const tenantId = useTenant().id

  const { data } = useAvailableResourcesQuery({
    variables: {
      tenantId,
      propertyId,
      resourceTypeId,
      start,
      end,
    },
  })

  const options: LocalResource[] = useMemo(() => {
    const resources = [...(data?.getAllAvailableResources || [])]
    const optionResources = resources.map((r) => ({ id: r.id, code: r.code }))
    if (localValue) {
      if (!optionResources.find(({ id }) => id === localValue.id)) optionResources.push({ ...localValue })
    }
    const sortedResources = optionResources.sort((a, b) => (a.code > b.code ? 1 : a.code !== b.code ? -1 : 0))
    return sortedResources
  }, [data?.getAllAvailableResources, localValue])

  return (
    <Autocomplete //
      fullWidth
      options={options}
      disableClearable={disableClearable}
      renderInput={(params) => (
        <TextField //
          {...params}
          sx={{ height: 38 }}
        />
      )}
      isOptionEqualToValue={(option, value) => option?.code === value?.code && option?.id === value?.id}
      getOptionLabel={(option) => option?.code || '-'}
      value={localValue ?? { code: '', id: '' }}
      onChange={(event, value) => {
        const newValue: ResourceRow = { type: 'LOADING', resource: value }
        apiRef.current.setEditCellValue({ id, field, value: newValue })
      }}
    />
  )
}
